import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { CLASSIFICATION_LABELS } from '@/features/inspection/modification-state-rules';
import { ROUTES } from '@/routes/routes';
import type { StaffRequestThread, ThreadRevision } from '@/services/request-thread.service';

/**
 * The two compact context cards: the JOB this request belongs to, and the
 * QUOTATION it was raised against.
 *
 * Both are deliberately thin. A Request Thread is not an inspection detail page
 * and it is not the quotation module: it shows enough to know which job and
 * which paper are involved, and links out for everything else. Every value below
 * comes from the ONE composed read — there is no second fetch for the job and no
 * second fetch for the revision, which is precisely what the composed endpoint
 * exists to make unnecessary.
 *
 * The quotation card shows identity only — reference, revision number, status,
 * date. No amount (none exists in the domain), no asset id, no storage key. The
 * server does not send them and this must never start asking for them.
 */

function Card({
  title,
  children,
  action,
}: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2.5">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </h2>
        {action}
      </div>
      <div className="space-y-2.5 px-4 py-3">{children}</div>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right text-sm text-foreground">{children}</span>
    </div>
  );
}

/** An em dash, so a missing value reads as "we do not have one" rather than as a
 * rendering failure. Null priority is a real answer and is shown as one. */
const DASH = '—';

export function InspectionContextCard({ thread }: { thread: StaffRequestThread }) {
  const job = thread.inspection;
  return (
    <Card
      title="Job"
      action={
        <Link
          to={ROUTES.INSPECTION_DETAIL.replace(':id', job.id)}
          className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
        >
          Open job
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3 w-3" />
        </Link>
      }
    >
      <Row label="Job number">
        <span className="font-medium" data-testid="thread-inspection-number">
          {job.inspectionNumber}
        </span>
      </Row>
      <Row label="Customer">{job.customerName ?? DASH}</Row>
      <Row label="Equipment">{job.equipmentName ?? DASH}</Row>
      <Row label="Job status">{job.status}</Row>
      <Row label="Priority">
        <span data-testid="thread-priority">
          {job.priority ? (
            <span className="inline-flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: job.priority.colorHex }}
              />
              {job.priority.name}
            </span>
          ) : (
            DASH
          )}
        </span>
      </Row>
    </Card>
  );
}

/** Exported for Stage E-iv so the timeline's revision marker renders revision
 *  identity through the SAME component as the quotation card — one revision
 *  presentation, not two that could drift. */
export function RevisionLine({ revision }: { revision: ThreadRevision }) {
  return (
    <>
      <span className="font-medium">{revision.quotationReference ?? 'No reference'}</span>
      <span className="block text-xs text-muted-foreground">
        Revision {revision.revisionNumber} · {revision.status}
        {revision.sentAt ? ` · sent ${new Date(revision.sentAt).toLocaleDateString()}` : ''}
      </span>
    </>
  );
}

export function QuotationContextCard({ thread }: { thread: StaffRequestThread }) {
  const { against, resulting } = thread.revisions;
  return (
    <Card title="Quotation">
      <Row label="Requested against">
        <span data-testid="thread-against-revision">
          <RevisionLine revision={against} />
        </span>
      </Row>
      <Row label="Resulting">
        <span data-testid="thread-resulting-revision">
          {resulting ? (
            <RevisionLine revision={resulting} />
          ) : (
            // Not "none" — a request that has not produced a revision is the
            // ordinary case, and saying so is clearer than an em dash here.
            <span className="text-muted-foreground">No revised quotation issued</span>
          )}
        </span>
      </Row>
      {thread.classification && (
        <Row label="Classified as">{CLASSIFICATION_LABELS[thread.classification]}</Row>
      )}
    </Card>
  );
}
